// Hackathon, now as a popup instead of a separate page. Opened from the
// Leaderboard (and anywhere else that fires `open-hackathon`). The pot,
// join CTA and recent winners live here so progress is always tracked
// next to the scoreboard — no page detour.
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Trophy,
  Crown,
  Calendar,
  Sparkles,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { useHackathon, formatUsd } from "@/hooks/useHackathon";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

export default function HackathonDialog({ open, onOpenChange }: Props) {
  const { user } = useAuth();
  const { pot, monthly, annual, joined, loading, join, leave } = useHackathon({
    scoreboardLimit: 0,
  });
  const [busy, setBusy] = useState(false);

  const potLabel = pot ? formatUsd(pot.pot_cents) : "$10,000";
  const paid = pot?.paid_users ?? 0;
  const threshold = pot?.unlock_threshold_users ?? 1000;
  const progressPct = Math.min(100, Math.round((paid / threshold) * 100));

  const handleJoin = async () => {
    if (!user) {
      toast.error("Sign in to join the hackathon");
      return;
    }
    setBusy(true);
    const { error } = await join();
    setBusy(false);
    if (error) toast.error(error);
    else toast.success("You're in. Build something great.");
  };

  const handleLeave = async () => {
    setBusy(true);
    await leave();
    setBusy(false);
    toast("You left the hackathon. Come back anytime.");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <Badge variant="outline" className="font-mono text-[10px] w-fit">
            <Sparkles className="w-3 h-3 mr-1 text-primary" />
            Always-on · Tracked on the leaderboard
          </Badge>
          <DialogTitle className="font-mono text-2xl">
            The HackerLab <span className="text-primary">Hackathon</span>
          </DialogTitle>
          <DialogDescription className="text-sm">
            Build, ship, and teach on hackerlabs.ai. The $10,000 pot unlocks at
            1,000 paid members, then pays out on a rolling basis to whoever's
            on top of the live scoreboard. The hacker who builds the most
            across the year takes home Hacker of the Year.
          </DialogDescription>
        </DialogHeader>

        {/* Pot */}
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-4">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            Current pot
          </div>
          <div className="text-3xl font-mono font-extrabold text-primary tabular-nums mt-1">
            {potLabel}
          </div>
          <div className="mt-2 text-[10px] font-mono text-muted-foreground">
            Paid users: {paid.toLocaleString()} / {threshold.toLocaleString()}
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-2 text-[9px] font-mono text-muted-foreground/70 leading-relaxed">
            +$10 per paid user for the first {threshold.toLocaleString()}, +$5
            per paid user thereafter. Score = XP × 1 + recent XP × 2 +
            published guides × 50 + projects × 75.
          </div>
        </div>

        {/* Join controls */}
        <div className="flex flex-wrap gap-2">
          {joined ? (
            <>
              <Button disabled className="font-mono">
                <CheckCircle2 className="w-4 h-4 mr-2" /> You're in
              </Button>
              <Button
                variant="outline"
                className="font-mono"
                onClick={handleLeave}
                disabled={busy}
              >
                Leave hackathon
              </Button>
            </>
          ) : (
            <Button
              onClick={handleJoin}
              disabled={busy || loading}
              className="font-mono"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trophy className="w-4 h-4 mr-2" />
              )}
              Join Hackathon
            </Button>
          )}
        </div>

        {/* Winners */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-primary" />
              <h3 className="font-mono font-extrabold text-sm">Recent winners</h3>
            </div>
            {monthly.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No payouts yet. The first winner will appear here.
              </p>
            ) : (
              <ul className="space-y-1.5 text-xs font-mono">
                {monthly.slice(0, 5).map((w) => (
                  <li
                    key={w.id}
                    className="flex items-center justify-between gap-2 border-b border-border/40 pb-1.5 last:border-none"
                  >
                    <span className="text-muted-foreground">
                      {MONTHS[w.month - 1]} {w.year}
                    </span>
                    <span className="text-foreground truncate">
                      {w.user_id.slice(0, 8)}…
                    </span>
                    <span className="text-primary font-semibold">
                      {formatUsd(w.prize_cents)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4 text-primary" />
              <h3 className="font-mono font-extrabold text-sm">Hacker of the Year</h3>
            </div>
            {annual.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                The first Hacker of the Year will be crowned at year-end.
              </p>
            ) : (
              <ul className="space-y-1.5 text-xs font-mono">
                {annual.slice(0, 5).map((w) => (
                  <li
                    key={w.id}
                    className="flex items-center justify-between gap-2 border-b border-border/40 pb-1.5 last:border-none"
                  >
                    <span className="text-muted-foreground">{w.year}</span>
                    <span className="text-foreground">
                      {w.user_id.slice(0, 8)}…
                    </span>
                    <span className="text-primary font-semibold">
                      {formatUsd(w.prize_cents)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Convenience wrapper: listens to the global `open-hackathon` window event so
// any surface (menu, banner, CTA) can pop this open without prop-drilling.
export function HackathonDialogPortal() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-hackathon", handler);
    return () => window.removeEventListener("open-hackathon", handler);
  }, []);
  return <HackathonDialog open={open} onOpenChange={setOpen} />;
}